// client/src/components/secure/training/FlashcardDeck.js — a flash-card lesson: tap to flip,
// previous/next, shuffle, and "Got it / Again" (a simple spaced repeat for this session:
// "Again" brings the card back a few cards later). onComplete fires once every card has been
// marked "Got it". Keys: Space flip, ← → move, G got it, A again, S shuffle. Swipe on phones.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Btn } from './shared';
import {
    deckAgain, deckCurrent, deckDone, deckFlip, deckGot, deckInit, deckKeyAction, deckNext, deckPrev, deckShuffle, swipeDirection,
} from './slideLogic';

const FlashcardDeck = ({ content, onComplete, completed }) => {
    const cards = (content && Array.isArray(content.cards)) ? content.cards : [];
    const [s, setS] = useState(() => deckInit(cards.length));
    const doneRef = useRef(!!completed);
    const touch = useRef(null);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!doneRef.current && deckDone(s)) { doneRef.current = true; onComplete && onComplete(); }
    }, [s, onComplete]);

    const act = useCallback((a) => {
        if (a === 'flip') setS(deckFlip);
        else if (a === 'next') setS(deckNext);
        else if (a === 'prev') setS(deckPrev);
        else if (a === 'got') setS(deckGot);
        else if (a === 'again') setS((x) => deckAgain(x, 3));
        else if (a === 'shuffle') setS((x) => deckShuffle(x));
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
            const tag = (e.target && e.target.tagName) || '';
            if (/^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(tag) || (e.target && e.target.isContentEditable)) return;
            if (boxRef.current && !document.body.contains(boxRef.current)) return;
            const a = deckKeyAction(e.key);
            if (a) { e.preventDefault(); act(a); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [act]);

    if (!cards.length) return <p className="text-sm text-slate-600">This lesson has no cards yet.</p>;
    const cur = deckCurrent(s);
    const card = cur === null ? null : cards[cur];
    const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
    const onTouchEnd = (e) => {
        if (!touch.current) return;
        const t = e.changedTouches[0];
        const dir = swipeDirection(t.clientX - touch.current.x, t.clientY - touch.current.y);
        touch.current = null;
        if (dir) { e.preventDefault(); act(dir); }
    };

    return (
        <div ref={boxRef} className="space-y-3">
            <div className="flex flex-wrap justify-between text-sm text-slate-600 gap-2">
                <span>Got it: <strong>{s.got.length}</strong> of {s.total}</span>
                <span>{s.queue.length} to go{s.again ? ` · "Again" ${s.again}×` : ''}</span>
            </div>
            <div className="h-2 bg-slate-200 rounded" role="progressbar" aria-valuemin={0} aria-valuemax={s.total} aria-valuenow={s.got.length} aria-label="Cards learned">
                <div className="h-2 bg-emerald-600 rounded" style={{ width: `${s.total ? Math.round((s.got.length / s.total) * 100) : 0}%` }} />
            </div>
            {card ? (
                <button type="button" onClick={() => act('flip')} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                    aria-label={s.flipped ? 'Answer side — tap to see the question' : 'Question side — tap to see the answer'}
                    className={`w-full rounded-lg shadow p-6 sm:p-10 text-center flex flex-col items-center justify-center transition-colors ${s.flipped ? 'bg-emerald-50 border border-emerald-300' : 'bg-indigo-50 border border-indigo-300'}`}
                    style={{ minHeight: '14rem' }}>
                    <span className="text-xs uppercase tracking-wider text-slate-500 mb-2">{s.flipped ? 'Answer' : 'Question'} · card {s.pos + 1} of {s.queue.length}</span>
                    <span className={`${s.flipped ? 'text-base sm:text-lg text-slate-800' : 'text-xl sm:text-2xl font-semibold text-slate-900'}`}>{s.flipped ? card.back : card.front}</span>
                    <span className="text-xs text-slate-500 mt-4">{s.flipped ? 'Did you know it?' : 'Tap to flip'}</span>
                </button>
            ) : (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-6 text-center">
                    <p className="font-semibold text-emerald-800">All {s.total} cards learned.</p>
                    <Btn kind="ghost" className="mt-3" onClick={() => setS(deckInit(cards.length))}>Go through them again</Btn>
                </div>
            )}
            {card && (
                <div className="flex flex-wrap gap-2 justify-between">
                    <div className="flex gap-2">
                        <Btn kind="ghost" onClick={() => act('prev')} aria-label="Previous card">◀</Btn>
                        <Btn kind="ghost" onClick={() => act('next')} aria-label="Next card">▶</Btn>
                        <Btn kind="ghost" onClick={() => act('shuffle')}>Shuffle</Btn>
                    </div>
                    <div className="flex gap-2">
                        <Btn kind="ghost" onClick={() => act('again')}>Again</Btn>
                        <Btn kind="ok" onClick={() => act('got')}>Got it</Btn>
                    </div>
                </div>
            )}
            <p className="text-xs text-slate-500 hidden sm:block">Keys: Space flip · ← → move · G got it · A again · S shuffle.</p>
        </div>
    );
};

export default FlashcardDeck;
